import {defineStore, acceptHMRUpdate} from 'pinia'
import {computed, ref} from "vue";
import {useApi} from '@/stores/api'
import {useMain} from "@/stores/main";
import {useTrainee} from "@/models/useTrainee";
import {useAuth} from "@/stores/auth";

export const useTrainees = defineStore(`trainees`, () => {
    const mainStore = useMain()
    const apiStore = useApi()

    /**
     * States
     * @type {RemovableRef<string>}
     */
    const $records = ref({})

    function $reset() {
        $records.value = {}
    }

    /**
     * Getters
     */
    const records = computed(() => $records.value)
    const hasRecord = computed(() => Object.keys($records.value).length > 0)

    const getById = async (id, force = false) => {
        if (undefined !== $records.value[`${id}`]) {
            if(force) {
                await $records.value[`${id}`].fetch()
            }
            return $records.value[`${id}`]
        }

        let trainee = useTrainee({id})
        await trainee.fetch()
        if (trainee.hasData) {
            return $records.value[`${id}`] = trainee
        }

        return false
    }

    const getByData = (data) => {

        if (undefined !== $records.value[`${data.id}`]) {
            $records.value[`${data.id}`].setData(data)
        } else {
            $records.value[`${data.id}`] = useTrainee(data)
        }

        return $records.value[`${data.id}`]
    }

    const fetch = async (options) => {
        options = {
            searchParams: {},
            ...options
        }

        try {
            const response = await apiStore.client().get('api/trainees',
                {searchParams: new URLSearchParams(options.searchParams)}
            ).json()

            // Build response
            response.data.forEach((data, index) => {
                $records.value[data.id] = useTrainee(data)
                response.data[index] = $records.value[data.id]
            })

            return response
        } catch (error) {
            await mainStore.catchCommonErrors(error)
            return false
        }

    }

    const destroy = async (trainee) => {
        const traineeId = typeof trainee === 'object' ? trainee.id : trainee
        try {
            const $trainee = await getById(traineeId)
            if ($trainee) {
                await $trainee.destroy()
                if (undefined !== $records.value[`${traineeId}`]) {
                    delete ($records.value[`${traineeId}`])
                }
                return true
            }
        } catch {
            return false
        }
        return false

    }

    const canAdd = computed(() => {
        const authStore = useAuth()
        return authStore.authUser.can('edit-others-trainees')
    })

    return {
        records,
        hasRecord,
        fetch,
        getById,
        getByData,
        destroy,
        canAdd,

        $reset,
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTrainees, import.meta.hot))
}
